import React from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
// Limitless imports
import { useExpertOnboardingIdVerification, useExpertOnboardingDocumentVerification } from "@limitless/expert";
import { useTranslation } from "@limitless/common/hooks";
import { LoadingSpinner } from "@limitless/portal";
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import TruliooClient from "@limitless/expert/utils/trulioo-client/TruliooClient";
import Button from "@limitless/modern/inputs/button/Button";

const ExpertDocumentVerificationOverview = ({ onboardingDetail }) => {
    const [iframeLoading, setIframLoading] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [isResultChecked, setIsResultChecked] = React.useState(false);

    const { t } = useTranslation();
    const { getExpertProfileFields, profileFields } = useExpertOnboardingIdVerification(t);
    const {
        sendDocumentVerificationTransactionId,
        userDocumentVerificationFields,
        getDocumentVerificationProfileFields
    } = useExpertOnboardingDocumentVerification();

    const {
        created,
        result,
        transactionId,
        loading,
        documentVerificationAttemptRemaining,
        error: graphError
    } = userDocumentVerificationFields;
    const { verificationConfigured, verified } = profileFields;
    const {
        onboarding: { totalChecks, completedChecks, documentVerificationCompleteChecks }
    } = onboardingDetail;

    const checkDcoumentVerificationCompleteCheck = documentVerificationCompleteChecks
        ? completedChecks === totalChecks
        : true;

    React.useEffect(() => getExpertProfileFields(), []);

    React.useEffect(() => {
        getDocumentVerificationProfileFields();
    }, [result]);

    React.useEffect(() => {
        if (
            verified &&
            checkDcoumentVerificationCompleteCheck &&
            (result === "nomatch" || !transactionId) &&
            documentVerificationAttemptRemaining !== 0
        ) {
            loadTruliooClient();
        }
    }, [result, verified, transactionId, documentVerificationAttemptRemaining]);

    const loadTruliooClient = () => {
        setIframLoading(true);
        const script = document.createElement("script");
        script.src = "https://js.trulioo.com/latest/main.js";
        script.async = true;
        script.onload = () => scriptLoaded();

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    };

    const scriptLoaded = () => {
        new TruliooClient({
            publicKey: process.env.REACT_APP_DOC_KEY,
            handleResponse,
            onInitialRenderComplete
        });
    };

    const onInitialRenderComplete = e => {
        if (e.status === 200 && e.statusText === "Success") {
            setIframLoading(false);
        } else {
            setIframLoading(false);
            setError(true);
        }
    };

    const handleResponse = e => {
        if (e.status === 200) {
            sendDocumentVerificationTransactionId(e.experienceTransactionId);
        } else {
            setError(true);
            loadTruliooClient();
        }
    };

    const handleCheckResultsClick = () => {
        sendDocumentVerificationTransactionId(transactionId);
        setIsResultChecked(true);
        setTimeout(() => {
            setIsResultChecked(false);
        }, 5000);
    };

    const headerText = React.useMemo(() => {
        if (!verified) {
            return "You cannot verify a document until you have verified your ID, please complete ID Verification.";
        } else if (!checkDcoumentVerificationCompleteCheck) {
            return "You cannot verify a document until all member details have successfully been completed and reviewed. Please try again later";
        } else if (created && !result) {
            return "Thank you for submitting a document for verification. This may take up to 5 minutes, so please check back here later, or try the button below.";
        } else if (result === "match") {
            return "Thank you for submitting a document for verification. Your document has been verified.";
        } else if (result === "nomatch") {
            return "Thank you for submitting a Document & Biometric Check. You have failed this check. Please try again";
        } else if (error) {
            return "Sorry, your request cannot be fulfilled at this time. Please try again.";
        } else if (graphError) {
            return "Something went wrong. Please try again.";
        } else {
            return "As part of the onboarding process for Certified Experts, Limitless will verify your ID document and compare a photo of you to the image in the ID document. Limitless uses a third party company for this and all data is destroyed once a result has been returned. Please follow the process below";
        }
    }, [created, result, error, verified, verificationConfigured, checkDcoumentVerificationCompleteCheck]);

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <Grid sx={{ maxWidth: "879px" }}>
            <Typography variant="body2">{headerText}</Typography>
            {created && !result ? (
                <>
                    <Typography variant="body2" sx={{ color: theme => theme.palette.text.secondary }} mt={2} mb={3}>
                        {`Date submitted: ${format(created, "DD/MM/YYYY HH:mm")}`}
                    </Typography>
                    {!isResultChecked ? (
                        <Button variant="contained" size="medium" onClick={handleCheckResultsClick}>
                            {"Check Results"}
                        </Button>
                    ) : (
                        <Typography variant="body2" sx={{ color: theme => theme.palette.error.main }}>
                            {"No results yet"}
                        </Typography>
                    )}
                </>
            ) : (
                <>
                    {result === "nomatch" &&
                        documentVerificationAttemptRemaining !== null &&
                        checkDcoumentVerificationCompleteCheck &&
                        verified && (
                            <Typography variant="body2" sx={{ color: theme => theme.palette.text.secondary }} mt={2}>
                                {`Number of attempts remaining: ${documentVerificationAttemptRemaining}`}
                            </Typography>
                        )}
                    {(!transactionId || result === "nomatch" || error) && (
                        <>
                            {iframeLoading && <LoadingSpinner />}
                            <div id="trulioo-embedid" style={{ width: 480 }} />
                        </>
                    )}
                </>
            )}
        </Grid>
    );
};

export default ExpertDocumentVerificationOverview;

ExpertDocumentVerificationOverview.propTypes = {
    onboardingDetail: PropTypes.object.isRequired
};
